import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];
const MODEL_IMG = "/assets/founder.jpg?v=4";

const CHAPTERS = [
  {
    n: "01",
    title: "The Origin",
    body: (
      <>
        DNA was born out of pure frustration with a broken market — filled with
        artificial glazes, cheap fillers and ingredients that leave you bloated
        and sluggish. We build nutrition the way ateliers build couture:
        deliberately, obsessively and by hand.
        <span className="block mt-5">
          No compromises. No stomach bloat. Just pure,{" "}
          <span className="text-gold-foil font-medium">Daily Nutrition Aesthetics.</span>
        </span>
      </>
    ),
  },
  {
    n: "02",
    title: "The Pursuit",
    body: "Our goal isn't just to make another protein bar — it's to build high-performance fuel for your everyday hustle. We combine proven ingredients like Creatine, Collagen, L-Glutamine, Ashwagandha and whole food bases into a fudgy, real-food base that actually tastes incredible. Pure function, zero bloat, made for those who demand excellence.",
  },
  {
    n: "03",
    title: "The Standard",
    body: (
      <>
        We refuse to compromise on quality so you don't have to compromise on
        performance. We set our benchmark on real, functional dosages, dual
        Whey + Pea protein bases, and rich whole-food ingredients. If a formula
        doesn't deliver peak digestability, exceptional taste, and proven
        science, it doesn't make it into a{" "}
        <span className="text-gold-foil font-medium">DNA</span> wrapper.
      </>
    ),
  },
];

const Manifesto = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [60, -60]);

  return (
    <section id="manifesto" ref={ref} className="py-32 md:py-48 border-b border-[#EBE8E3]" data-testid="manifesto-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-16 md:gap-8">
        <div className="md:col-span-5 relative">
          <motion.div style={{ y: yImg }} className="md:sticky md:top-32">
            <motion.h3
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1, ease: EASE }}
              className="font-serif-lux font-medium text-4xl md:text-6xl tracking-tighter leading-[0.95] mb-10"
              data-testid="manifesto-heading"
            >
              The Manifesto —
              <span className="italic font-light block mt-2">
                Why <span className="text-gold-foil font-medium not-italic">DNA</span> exists
              </span>
            </motion.h3>
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1.2, ease: EASE }}
              className="overflow-hidden"
            >
              <img
                src={MODEL_IMG}
                alt="Shubham Singh — Founder, DNA"
                data-testid="manifesto-model-img"
                className="w-full aspect-[3/4] object-cover"
              />
            </motion.div>
          </motion.div>
        </div>

        <div className="md:col-span-7 flex flex-col gap-24 md:gap-32 md:pt-24">
          {CHAPTERS.map((c, i) => (
            <motion.div
              key={c.n}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1, delay: 0.1, ease: EASE }}
              className={`max-w-lg ${i % 2 === 1 ? "md:ml-auto md:text-right" : ""}`}
              data-testid={`manifesto-chapter-${c.n}`}
            >
              <span className="font-serif-lux italic font-light text-6xl md:text-7xl text-[#C07C5F]">
                {c.n}.
              </span>
              <h3 className="font-serif-lux font-medium text-4xl md:text-5xl tracking-tighter mt-2 mb-6">
                {c.title}
              </h3>
              <p className="text-sm md:text-base font-light text-[#5C5C5C] leading-relaxed">
                {c.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Manifesto;
